import { createSlice } from "@reduxjs/toolkit";

import {
  createProjectAddon,
  deleteProjectAddon,
  getProjectAddons,
  updateProjectAddons
} from "./thunks";

import type { ProjectAddon, ProfilingLevel } from "./types";
import type {
  ProjectAddonUpdateResult,
  ProjectAddonsState,
  UpdateProjectAddonsRejection
} from "./types";
import type { PayloadAction } from "@reduxjs/toolkit";

const initialState: ProjectAddonsState = {
  data: {},
  error: {},
  loadingAddons: {},
  updating: false,
  profilingSelection: {}
};

const mergeAddonResults = (
  state: ProjectAddonsState,
  projectId: string,
  results: ProjectAddonUpdateResult[]
) => {
  if (!state.data[projectId]) {
    state.data[projectId] = { count: 0, items: [] };
  }
  const existing = state.data[projectId];

  for (const result of results) {
    if (result.kind === "remove") {
      existing.items = existing.items.filter(
        (a: ProjectAddon) => a.id !== result.addonId
      );
      continue;
    }

    const index = existing.items.findIndex(
      (a: ProjectAddon) => a.id === result.addon.id
    );
    if (index !== -1) {
      existing.items[index] = result.addon;
    } else {
      existing.items.push(result.addon);
    }
  }

  existing.count = existing.items.length;
};

const projectAddonsReducer = createSlice({
  name: "app/organization/project/addons",
  initialState,
  reducers: {
    setProfilingSelection: (
      state,
      action: PayloadAction<{ projectId: string; level: ProfilingLevel }>
    ) => {
      state.profilingSelection[action.payload.projectId] = action.payload.level;
    },
    clearProfilingSelection: (
      state,
      action: PayloadAction<{ projectId: string }>
    ) => {
      delete state.profilingSelection[action.payload.projectId];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getProjectAddons.pending, (state, { meta }) => {
        const { projectId } = meta.arg;
        state.loadingAddons[projectId] = true;
        delete state.error[projectId];
      })
      .addCase(getProjectAddons.fulfilled, (state, { meta, payload }) => {
        const { projectId } = meta.arg;
        state.data[projectId] = payload;
        state.loadingAddons[projectId] = false;
      })
      .addCase(getProjectAddons.rejected, (state, { meta, payload, error }) => {
        const { projectId } = meta.arg;
        state.loadingAddons[projectId] = false;
        // Store a guaranteed-truthy error. The API client rejects an empty-body
        // 4xx (e.g. the addons endpoint for project types that don't serve it,
        // like Adobe Commerce) with "", and useProjectAddons only stops
        // re-fetching while `error` is truthy — a falsy value here defeats that
        // guard and re-fetches forever.
        state.error[projectId] =
          payload || error || "Failed to load project addons";
      })
      .addCase(createProjectAddon.pending, (state, { meta }) => {
        const { projectId } = meta.arg;
        state.loadingAddons[projectId] = true;
        delete state.error[projectId];
      })
      .addCase(createProjectAddon.fulfilled, (state, { meta, payload }) => {
        const { projectId } = meta.arg;
        state.loadingAddons[projectId] = false;
        mergeAddonResults(state, projectId, [payload]);
        // The committed change is now the current level; drop the draft.
        delete state.profilingSelection[projectId];
      })
      .addCase(createProjectAddon.rejected, (state, { meta, payload }) => {
        const { projectId } = meta.arg;
        state.loadingAddons[projectId] = false;
        state.error[projectId] = payload;
      })
      .addCase(deleteProjectAddon.pending, (state, { meta }) => {
        const { projectId } = meta.arg;
        state.loadingAddons[projectId] = true;
        delete state.error[projectId];
      })
      .addCase(deleteProjectAddon.fulfilled, (state, { meta, payload }) => {
        const { projectId } = meta.arg;
        state.loadingAddons[projectId] = false;
        mergeAddonResults(state, projectId, [payload]);
        // The committed change is now the current level; drop the draft.
        delete state.profilingSelection[projectId];
      })
      .addCase(deleteProjectAddon.rejected, (state, { meta, payload }) => {
        const { projectId } = meta.arg;
        state.loadingAddons[projectId] = false;
        state.error[projectId] = payload;
      })
      .addCase(updateProjectAddons.pending, (state, { meta }) => {
        state.updating = true;
        delete state.error[meta.arg.projectId];
      })
      .addCase(updateProjectAddons.fulfilled, (state, { meta, payload }) => {
        state.updating = false;
        mergeAddonResults(state, meta.arg.projectId, payload);
      })
      .addCase(updateProjectAddons.rejected, (state, { meta, payload }) => {
        state.updating = false;
        const rejection = payload as UpdateProjectAddonsRejection | undefined;
        if (rejection?.partialResults) {
          mergeAddonResults(
            state,
            meta.arg.projectId,
            rejection.partialResults
          );
        }
        state.error[meta.arg.projectId] = rejection?.error;
      });
  }
});

export const { setProfilingSelection, clearProfilingSelection } =
  projectAddonsReducer.actions;
export default projectAddonsReducer.reducer;
